"use client";

import { Bell, CircleUser, EllipsisVertical, FileDown, FileUp, Keyboard, LogIn, LogOut, Menu, Moon, Shield, Star, Sun } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/context/AuthContext";
import { useAuthModal } from "@/lib/context/AuthModalContext";
import { useChrome } from "./ChromeContext";
import { useEditor } from "@/components/editor/EditorContext";
import { useUnsavedGuard } from "@/components/editor/UnsavedChangesGuard";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { useTheme } from "./ThemeContext";
import { locales, type Locale } from "@/i18n/config";
import { ShortcutTooltip } from "@/components/ui/ShortcutTooltip";
import { ShortcutsOverlay } from "@/components/shortcuts/ShortcutsOverlay";

const SpadeLogo = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden>
    <path
      d="M8 1.5 L13.5 8 Q13.5 11 11 11 Q9 11 8.5 9.5 L9.5 14 H6.5 L7.5 9.5 Q7 11 5 11 Q2.5 11 2.5 8 Z"
      fill="currentColor"
    />
  </svg>
);

export function TopNav() {
  const t = useTranslations("nav");
  const tc = useTranslations("common");
  const tt = useTranslations("theme");
  const { setSidebarOpen, setImportOpen, setExportOpen } = useChrome();
  const editor = useEditor();
  const canExport = editor !== null;
  const { user, profile, signOut, isAdmin } = useAuth();
  const { openAuthModal } = useAuthModal();
  const pathname = usePathname();
  const router = useRouter();
  const { guardedAction } = useUnsavedGuard();

  const activeTab = pathname.startsWith("/strategy")
    ? "strategy"
    : pathname.startsWith("/trainer")
      ? "trainer"
      : "library";
  const canImport = pathname.startsWith("/ranges");

  // For mobile dropdown: locale switching
  const locale = useLocale();
  const [isPending, startTransition] = useTransition();
  const nextLocale: Locale = locales.find((l) => l !== locale) ?? locales[0];
  function switchLocale() {
    document.cookie = `NEXT_LOCALE=${nextLocale};path=/;max-age=31536000`;
    startTransition(() => router.refresh());
  }

  // For mobile dropdown: theme toggling
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  return (
    <>
    <header className="relative flex h-12 flex-shrink-0 items-center gap-3 border-b border-line bg-bg-2 px-3 sm:px-4">
      {/* Hamburger — mobile/tablet only, opens library Sheet */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={() => setSidebarOpen(true)}
        aria-label={t("openLibrary")}
        aria-controls="library-sheet"
      >
        <Menu className="h-4 w-4" />
      </Button>

      {/* Logo + wordmark */}
      <Link href="/" className="flex items-center gap-2.5">
        <div className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-accent text-accent-fg">
          <SpadeLogo />
        </div>
        <span className="hidden sm:inline text-sm font-semibold tracking-tight">{t("brandName")}</span>
      </Link>

      <div className="flex-1" />

      {/* Mode switcher — aligned with sidebar edge on lg+, centered on mobile */}
      <div role="tablist" className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 lg:left-[var(--sidebar-w)] lg:translate-x-0 flex items-center gap-0.5">
        {([
          { key: "library",  label: t("modeLibrary"),  href: "/ranges" },
          { key: "strategy", label: t("modeStrategy"), href: "/strategy" },
          { key: "trainer",  label: t("modeTrainer"),  href: "/trainer" },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => guardedAction(() => router.push(tab.href))}
            className={`rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors duration-150 ${
              activeTab === tab.key
                ? "text-accent bg-panel"
                : "text-fg-dim hover:text-accent hover:bg-panel"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Right cluster (3-tier responsive) ── */}

      {/* Action buttons — icon-only at md, full labels at xl */}
      {user && (
        <div className="hidden md:flex items-center gap-1.5 xl:gap-3">
          <Button variant="outline" size="sm" className="xl:px-3 px-2" onClick={() => setImportOpen(true)} disabled={!canImport} title={tc("import")}>
            <FileUp className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">{tc("import")}</span>
          </Button>
          <Button variant="outline" size="sm" className="xl:px-3 px-2" onClick={() => setExportOpen(true)} disabled={!canExport} title={tc("export")}>
            <FileDown className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">{tc("export")}</span>
          </Button>
          <Button size="sm" className="xl:px-3 px-2" title={t("upgradePlan")}>
            <Star className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">{t("upgradePlan")}</span>
          </Button>
          <div className="h-5 w-px bg-line-2" />
        </div>
      )}

      {/* Utility icons — visible at md+ */}
      <div className="hidden md:flex items-center gap-3">
        <LocaleSwitcher />
        <ThemeToggle />
        <ShortcutTooltip label="Hotkeys" shortcut="H">
          <button
            type="button"
            onClick={() => setShortcutsOpen(true)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-dim hover:bg-panel-2 hover:text-fg"
          >
            <Keyboard className="h-4 w-4" />
          </button>
        </ShortcutTooltip>

        {user && (
          <button
            type="button"
            title={t("notifications")}
            className="relative inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-dim hover:bg-panel-2 hover:text-fg"
          >
            <Bell className="h-4 w-4" />
            <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-raise" />
          </button>
        )}

        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold text-accent-fg"
                style={{ background: "linear-gradient(135deg, var(--call), #4a6535)" }}
                title={profile?.display_name ?? profile?.username ?? user.email ?? ""}
                aria-label={t("userAvatar")}
              >
                {(profile?.display_name ?? profile?.username ?? user.email ?? "U").charAt(0).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem disabled className="text-xs text-fg-mute">
                {profile?.username ?? user.email ?? ""}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isAdmin && (
                <DropdownMenuItem onClick={() => guardedAction(() => router.push("/admin"))}>
                  <Shield className="mr-2 h-4 w-4" />
                  {t("admin")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => signOut()}>
                <LogOut className="mr-2 h-4 w-4" />
                {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button variant="outline" size="sm" onClick={() => openAuthModal()}>
            <LogIn className="h-3.5 w-3.5" />
            {t("signIn")}
          </Button>
        )}
      </div>

      {/* Mobile — visible below md */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden">
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {user && (
            <>
              <DropdownMenuItem onClick={() => setImportOpen(true)} disabled={!canImport}>
                <FileUp className="mr-2 h-4 w-4" />
                {tc("import")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setExportOpen(true)} disabled={!canExport}>
                <FileDown className="mr-2 h-4 w-4" />
                {tc("export")}
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Star className="mr-2 h-4 w-4" />
                {t("upgradePlan")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={() => setTheme(isDark ? "light" : "dark")}>
            {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
            {isDark ? tt("switchToLight") : tt("switchToDark")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={switchLocale} disabled={isPending}>
            <span className="mr-2 w-4 text-center text-xs font-medium uppercase">{nextLocale}</span>
            {nextLocale === "ru" ? "Русский" : "English"}
          </DropdownMenuItem>
          {user && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Bell className="mr-2 h-4 w-4" />
                {t("notifications")}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          {user ? (
            <>
              <DropdownMenuItem disabled className="text-xs text-fg-mute">
                <CircleUser className="mr-2 h-4 w-4" />
                {profile?.username ?? user.email ?? ""}
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => guardedAction(() => router.push("/admin"))}>
                  <Shield className="mr-2 h-4 w-4" />
                  {t("admin")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => signOut()}>
                <LogOut className="mr-2 h-4 w-4" />
                {t("signOut")}
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem onClick={() => openAuthModal()}>
              <LogIn className="mr-2 h-4 w-4" />
              {t("signIn")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

    </header>
    <ShortcutsOverlay open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </>
  );
}
